/*
 * Usage:
 * - This file exposes window.publishShopifyEvent(eventName, payload[, logFn]).
 * - Call publishShopifyEvent from theme JS to send custom events to Shopify Pixels.
 * - Example: publishShopifyEvent('event_name', { key: 'value' });
 */
(function () {
  // Global helper to publish events for Shopify custom pixels.
  function publishShopifyEvent(eventName, payload, logFn) {
    if (window.Shopify?.analytics?.publish) {
      Shopify.analytics.publish(eventName, payload);
      if (typeof logFn === "function") logFn(`Shopify.analytics.publish: ${eventName}`, payload);
      return true;
    }
    if (typeof logFn === "function") logFn("⚠️ Shopify.analytics.publish not available yet.");
    return false;
  }

  window.publishShopifyEvent = publishShopifyEvent;

  function getDynamicScrollThresholdPercent() {
    var body = document.body;
    var documentElement = document.documentElement;

    var documentHeight = Math.max(
      body.scrollHeight,
      body.offsetHeight,
      documentElement.clientHeight,
      documentElement.scrollHeight,
      documentElement.offsetHeight
    );

    var minHeightPx = 2000;
    var maxHeightPx = 18000;
    var maxPercent = 90;
    var minPercent = 50;

    if (documentHeight <= minHeightPx) return maxPercent;
    if (documentHeight >= maxHeightPx) return minPercent;

    var normalizedHeight = (documentHeight - minHeightPx) / (maxHeightPx - minHeightPx);
    var dropoffGamma = 0.5; // smaller = steeper early drop
    var thresholdPercentCalc =
      maxPercent + (minPercent - maxPercent) * Math.pow(normalizedHeight, dropoffGamma);

    return Math.round(thresholdPercentCalc);
  }

  function getScrollProgressPercent() {
    var documentElement = document.documentElement;
    var scrollTopPx = window.pageYOffset || documentElement.scrollTop || 0;
    var viewportHeightPx = window.innerHeight || documentElement.clientHeight || 0;
    var scrollHeightPx = documentElement.scrollHeight || 0;
    if (!scrollHeightPx) return 0;
    var scrolledPercent = ((scrollTopPx + viewportHeightPx) / scrollHeightPx) * 100;
    return Math.min(100, Math.max(0, Math.round(scrolledPercent)));
  }

  function trackShopifyPageLoad() {
    publishShopifyEvent("shopify_page_load", {
      timestamp: Date.now(),
      scrollHeight: document.documentElement.scrollHeight,
      page_path: window.location.pathname,
    });
  }

  function trackScrollDynamic() {
    var hasFired = false;
    var thresholdPercent = getDynamicScrollThresholdPercent();

    function handleScrollDynamic() {
      if (hasFired) return;
      var scrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
      var currentScrollPercent = getScrollProgressPercent();
      if (currentScrollPercent >= thresholdPercent) {
        hasFired = true;
        publishShopifyEvent("scroll_dynamic", {
          timestamp: Date.now(),
          thresholdPercent: thresholdPercent,
          scrollPercent: currentScrollPercent,
          scrollHeight: document.documentElement.scrollHeight,
          scrollTop: scrollTop,
          viewportHeight: window.innerHeight || document.documentElement.clientHeight || 0,
        });
        window.removeEventListener("scroll", handleScrollDynamic);
        window.removeEventListener("resize", handleScrollDynamic);
      }
    }

    window.addEventListener("scroll", handleScrollDynamic, { passive: true });
    window.addEventListener("resize", handleScrollDynamic);
    handleScrollDynamic();
  }

  function trackLinkClicks() {
    document.addEventListener(
      "click",
      function (event) {
        var link = event.target && event.target.closest ? event.target.closest("a") : null;
        if (!link) return;

        publishShopifyEvent("link_click", {
          timestamp: Date.now(),
          href: link.getAttribute("href") || "",
        });
      },
      true
    );
  }

  function trackActiveTime() {
    // Add/extend milestones here — fired in order as active time accumulates
    var ACTIVITY_MILESTONES = [
      { ms: 10000, name: "active_10s", fired: false },
      { ms: 20000, name: "active_20s", fired: false },
    ];

    var MAX_ACTIVITY_GAP_MS = 5000;
    var ACTIVITY_SAMPLE_MS = 250;
    var activeDurationMs = 0;
    var lastActivityAt = null;

    function isTrackableActivityState() {
      return !document.hidden && typeof document.hasFocus === "function" && document.hasFocus();
    }

    function resetActivityWindow() {
      lastActivityAt = null;
    }

    function allMilestonesFired() {
      return ACTIVITY_MILESTONES.every(function (m) { return m.fired; });
    }

    function maybePublishActiveEvent() {
      for (var i = 0; i < ACTIVITY_MILESTONES.length; i++) {
        var m = ACTIVITY_MILESTONES[i];
        if (!m.fired && activeDurationMs >= m.ms) {
          m.fired = true;
          publishShopifyEvent(m.name, {
            timestamp: Date.now(),
            activeDurationMs: activeDurationMs,
            thresholdMs: m.ms,
            page_path: window.location.pathname,
          });
        }
      }

      if (allMilestonesFired()) removeListeners();
    }

    function handleActivity() {
      if (allMilestonesFired() || !isTrackableActivityState()) {
        resetActivityWindow();
        return;
      }

      var now = Date.now();

      if (lastActivityAt !== null && now - lastActivityAt < ACTIVITY_SAMPLE_MS) return;

      if (lastActivityAt !== null) {
        var deltaMs = now - lastActivityAt;
        if (deltaMs > 0 && deltaMs <= MAX_ACTIVITY_GAP_MS) {
          activeDurationMs += deltaMs;
        }
      }

      lastActivityAt = now;
      maybePublishActiveEvent();
    }

    function handleVisibilityOrFocusChange() {
      if (!isTrackableActivityState()) resetActivityWindow();
    }

    function removeListeners() {
      window.removeEventListener("mousemove", handleActivity);
      window.removeEventListener("scroll", handleActivity);
      window.removeEventListener("keydown", handleActivity);
      window.removeEventListener("touchstart", handleActivity);
      window.removeEventListener("pointerdown", handleActivity);
      window.removeEventListener("focus", handleVisibilityOrFocusChange);
      window.removeEventListener("blur", handleVisibilityOrFocusChange);
      document.removeEventListener("visibilitychange", handleVisibilityOrFocusChange);
    }

    window.addEventListener("mousemove", handleActivity, { passive: true });
    window.addEventListener("scroll", handleActivity, { passive: true });
    window.addEventListener("keydown", handleActivity);
    window.addEventListener("touchstart", handleActivity, { passive: true });
    window.addEventListener("pointerdown", handleActivity, { passive: true });
    window.addEventListener("focus", handleVisibilityOrFocusChange);
    window.addEventListener("blur", handleVisibilityOrFocusChange);
    document.addEventListener("visibilitychange", handleVisibilityOrFocusChange);
  }

  function handleCapturedEmail(input, source, extra = {}) {
    var email = input.value.trim();
    if (!email) return;

    console.log("📧 Email captured:", {
      source: source,
      id: input.id,
      name: input.name,
      email: email,
      ...extra,
    });

    if (window.Shopify?.analytics?.publish) {
      Shopify.analytics.publish("email_captured", {
        email: email,
        source: source,
        field_id: input.id,
        field_name: input.name,
        ref_url: window.location.href,
        ...extra,
      });
    }
  }

  /**
   * 1. Normal DOM fields: Omnisend, regular inputs
   */
  document.addEventListener("focusout", function (event) {
    var input = event.target;

    if (!(input instanceof HTMLInputElement)) return;

    var isOmnisendEmail =
      input.matches('input[name="emailField"]') ||
      input.matches('input[type="email"]') ||
      input.matches('input[autocomplete="email"]');

    if (!isOmnisendEmail) return;

    handleCapturedEmail(input, "normal_dom");
  });

  /**
   * 2. Shopify Forms inside Shadow DOM
   */
  function attachShopifyFormsEmailCapture() {
    document.querySelectorAll("shopify-forms-embed").forEach(function (host) {
      if (host.dataset.emailFocusoutAttached === "true") return;

      var root = host.shadowRoot;
      if (!root) return;

      host.dataset.emailFocusoutAttached = "true";

      root.addEventListener("focusout", function (event) {
        var input = event.target;

        if (!(input instanceof HTMLInputElement)) return;

        var isShopifyEmail =
          input.matches("input#email") ||
          input.matches('input[data-testid="field-email"]') ||
          input.matches('input[inputmode="email"]') ||
          input.matches('input[type="email"]');

        if (!isShopifyEmail) return;

        handleCapturedEmail(input, "shopify_form_shadow_dom", {
          form_host_id: host.id,
        });
      });

      console.log("✅ Attached Shopify Forms listener:", host);
    });
  }

  attachShopifyFormsEmailCapture();

  new MutationObserver(attachShopifyFormsEmailCapture).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });

  window.addEventListener("load", function () {
    trackShopifyPageLoad();
    trackScrollDynamic();
    trackLinkClicks();
    trackActiveTime();
  });
})();
